import React, {
	useState,
	useRef,
	useEffect,
	useCallback,
	useMemo,
} from "react";
import {
	Box,
	CircularProgress,
	Typography,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Alert,
	Button,
} from "@mui/material";
import Header from "../components/Headers/Header";
import SupplyTypeAllocationDashboard from "../components/Dashboard/SupplyTypeAllocationDashboard";
import VffDashboard from "../components/Dashboard/VffDashboard";
import SeriesDashboard from "../components/Dashboard/SeriesDashboard";
import VffDashboardJoyrideTour from "../components/JoyrideTours/VffDashboardJoyrideTour";
import SupplyTypeAllocationDashboardJoyrideTour from "../components/JoyrideTours/SupplyTypeAllocationDashboardJoyrideTour";
import SeriesTrackingDashboardJoyrideTour from "../components/JoyrideTours/SeriesTrackingDashboardJoyrideTour";
import FloatingExportModal from "../components/Modals/FloatingExportModal";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getApiBaseUrl } from "../utils/apiConfig";
import { apiClient } from "../services/api";
import { signOut } from "../utils/authorizationUtils";

// Register ChartJS components
ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ChartDataLabels,
);

// MAN brand colors
const MAN_COLORS = [
	{ bg: "#303c49", border: "#1a2129" }, // Dark blue
	{ bg: "#ff0000", border: "#b30000" }, // Red
	{ bg: "#4489c5", border: "#3c7ab1" }, // Light blue
	{ bg: "#6b9700", border: "#5c8300" }, // Green
	{ bg: "#ffa500", border: "#e6a300" }, // Yellow
	{ bg: "#161616", border: "#0d0d0d" }, // Black
];

// VFF-specific colors optimized for status_color values
// Updated to handle the new status_color field from kast_vehicle_frontend table
const VFF_COLOR_MAP = {
	orange: { bg: "#ffcd00", border: "#e6a300" }, // Nomination missing
	yellow: { bg: "#ffff00", border: "#cccc00" }, // Delivery date missing
	available: { bg: "#91b900", border: "#7ca000" }, // green for available
	red: { bg: "#ff0000", border: "#b30000" },
	blue: { bg: "#4489c5", border: "#3c7ab1" },
	green: { bg: "#91b900", border: "#7ca000" },
	gray: { bg: "#6b7280", border: "#5b6370" },
	grey: { bg: "#6b7280", border: "#5b6370" },
	purple: { bg: "#800080", border: "#660066" },
	// Legacy support for German names and older ampel_teileanflug values
	tra: { bg: "#858585", border: "#0d0d0d" }, // "tra" (transparent) shown as light grey
	transparent: { bg: "#858585", border: "#0d0d0d" },
	blau: { bg: "#4489c5", border: "#3c7ab1" }, // blue
	gelb: { bg: "#ffff00", border: "#cccc00" }, // yellow / delivery date missing
	grau: { bg: "#6b7280", border: "#5b6370" }, // gray
	grün: { bg: "#91b900", border: "#7ca000" }, // green (with umlaut)
	grun: { bg: "#91b900", border: "#7ca000" }, // green (without umlaut fallback)
	rot: { bg: "#ff0000", border: "#b30000" }, // red
	lila: { bg: "#800080", border: "#660066" }, // purple
};

function getVffColor(type) {
	if (!type) return { bg: "#303c49", border: "#1a2129" }; // fallback dark blue
	const key = type.toString().trim().toLowerCase();
	return VFF_COLOR_MAP[key] || { bg: "#303c49", border: "#1a2129" }; // fallback
}

const DashboardPage = ({ user } = {}) => {
	useEffect(() => {
		document.title = "MAN PTT - Dashboard";
	}, []);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [mainData, setMainData] = useState([]);
	const [mainChartData, setMainChartData] = useState(null);
	const [vffData, setVffData] = useState([]);
	const [vffChartData, setVffChartData] = useState(null);
	const [filterOptions, setFilterOptions] = useState(null);
	const [totalParts, setTotalParts] = useState(0);
	const [purchaseTypeCounts, setPurchaseTypeCounts] = useState({});
	const [seTeamCounts, setSeTeamCounts] = useState({});
	const [combinedData, setCombinedData] = useState({});
	const [selectedPage, setSelectedPage] = useState(() => {
		// Restore the last viewed dashboard on refresh instead of defaulting to "main"
		try {
			const saved = localStorage.getItem("dashboardSelectedPage");
			if (saved === "main" || saved === "vff" || saved === "series") {
				return saved;
			}
		} catch (e) {
			// localStorage may be unavailable (private mode, etc.) - fall back to default
		}
		return "main";
	});

	// Request deduplication refs
	const requestInProgressRef = useRef({
		mainData: false,
		vffData: false,
		filterOptions: false,
	});

	// Initial fetch tracking refs to prevent duplicate initial data loads
	const initialFetchCompletedRef = useRef({
		main: false,
		vff: false,
	});

	// Flag to track strict mode double mounting
	const strictModeInitialRenderDoneRef = useRef(false);
	const [anflugTypes, setAnflugTypes] = useState([]);
	const [vehicleNumbers, setVehicleNumbers] = useState([]);
	const [programms, setProgramms] = useState([]);
	const [programmVehicleMapping, setProgrammVehicleMapping] = useState({});
	const [vffPartNumbers, setVffPartNumbers] = useState([]);
	const [vffPurchaseTypes, setVffPurchaseTypes] = useState([]);
	const [vffFilterOptions, setVffFilterOptions] = useState(null);
	// Main dashboard states
	const [selectedAnflug, setSelectedAnflug] = useState("all");
	const [selectedVehicles, setSelectedVehicles] = useState(["all"]);
	const [selectedTeams, setSelectedTeams] = useState(["all"]);
	const [selectedProject, setSelectedProject] = useState(["all"]);
	const [selectedProgramm, setSelectedProgramm] = useState(["all"]);
	const [selectedPurchaseType, setSelectedPurchaseType] = useState(["all"]);
	const [selectedCategory, setSelectedCategory] = useState(["all"]);
	const [selectedVffProgramm, setSelectedVffProgramm] = useState(["all"]);
	const [selectedVffPartNo, setSelectedVffPartNo] = useState(["all"]);
	const [selectedVffPurchaseType, setSelectedVffPurchaseType] = useState([
		"all",
	]);

	// Temporary states for filters (before applying)
	const [tempSelectedTeams, setTempSelectedTeams] = useState(["all"]);
	const [tempSelectedProject, setTempSelectedProject] = useState(["all"]);
	const [tempSelectedProgramm, setTempSelectedProgramm] = useState(["all"]);
	const [tempSelectedPurchaseType, setTempSelectedPurchaseType] = useState([
		"all",
	]);
	const [tempSelectedCategory, setTempSelectedCategory] = useState(["all"]);
	const [tempSelectedAnflug, setTempSelectedAnflug] = useState("all");
	const [tempSelectedVehicles, setTempSelectedVehicles] = useState(["all"]);
	const [tempSelectedVffProgramm, setTempSelectedVffProgramm] = useState([
		"all",
	]);
	const [tempSelectedVffPartNo, setTempSelectedVffPartNo] = useState(["all"]);
	const [tempSelectedVffPurchaseType, setTempSelectedVffPurchaseType] =
		useState(["all"]);

	// Loading states
	const [isChartLoading, setIsChartLoading] = useState(false);

	// VFF dashboard states
	const [vffTotalVehicles, setVffTotalVehicles] = useState(0);
	const [vffTotalAnflugTypes, setVffTotalAnflugTypes] = useState(0);
	const [vffTotalParts, setVffTotalParts] = useState(0);
	// Temporary flag to show/hide series dashboard
	const showSeriesDashboard = true;

	// Floating Series Dashboard export state (reported up from SeriesDashboard).
	const [seriesFloatingState, setSeriesFloatingState] = useState({
		floatingMode: false,
		projectNumber: [],
		alg: [],
		changeNumber: [],
	});
	const [floatingExportOpen, setFloatingExportOpen] = useState(false);

	// Dashboard guided tour state. The User Guide entry in the profile dropdown
	// opens the guide for the currently active dashboard view: the Supply Type
	// Allocation tour on the "main" view, the Vehicle Tracking tour on "vff",
	// and the Series Tracking tour on "series".
	const [isVffTourOpen, setIsVffTourOpen] = useState(false);
	const [isStaTourOpen, setIsStaTourOpen] = useState(false);
	const [isSeriesTourOpen, setIsSeriesTourOpen] = useState(false);
	const handleStartTour = useCallback(() => {
		if (selectedPage === "vff") {
			setIsVffTourOpen(true);
		} else if (selectedPage === "main") {
			setIsStaTourOpen(true);
		} else if (selectedPage === "series") {
			setIsSeriesTourOpen(true);
		}
	}, [selectedPage]);
	const handleCloseTour = useCallback(() => {
		setIsVffTourOpen(false);
	}, []);
	const handleCloseStaTour = useCallback(() => {
		setIsStaTourOpen(false);
	}, []);
	const handleCloseSeriesTour = useCallback(() => {
		setIsSeriesTourOpen(false);
	}, []);

	// Persist the selected dashboard so a page refresh keeps the user where they were
	useEffect(() => {
		try {
			localStorage.setItem("dashboardSelectedPage", selectedPage);
		} catch (e) {
			// Ignore storage errors (e.g. private browsing)
		}
	}, [selectedPage]);

	// Handle URL parameters to set initial view
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const viewParam = urlParams.get("view");
		if (viewParam === "vff") {
			setSelectedPage("vff");
			// Clear the URL parameter to clean up the URL
			window.history.replaceState({}, document.title, window.location.pathname);
		} else if (viewParam === "series") {
			setSelectedPage("series");
			// Clear the URL parameter to clean up the URL
			window.history.replaceState({}, document.title, window.location.pathname);
		} else if (viewParam === "main") {
			setSelectedPage("main");
			// Clear the URL parameter to clean up the URL
			window.history.replaceState({}, document.title, window.location.pathname);
		}
	}, []);

	// Check if filters have changed
	const hasFiltersChanged = useMemo(() => {
		return (
			JSON.stringify(tempSelectedTeams) !== JSON.stringify(selectedTeams) ||
			JSON.stringify(tempSelectedProject) !== JSON.stringify(selectedProject) ||
			JSON.stringify(tempSelectedProgramm) !==
				JSON.stringify(selectedProgramm) ||
			JSON.stringify(tempSelectedPurchaseType) !==
				JSON.stringify(selectedPurchaseType) ||
			JSON.stringify(tempSelectedCategory) !== JSON.stringify(selectedCategory)
		);
	}, [
		tempSelectedTeams,
		selectedTeams,
		tempSelectedProject,
		selectedProject,
		tempSelectedProgramm,
		selectedProgramm,
		tempSelectedPurchaseType,
		selectedPurchaseType,
		tempSelectedCategory,
		selectedCategory,
	]);
	const hasVffFiltersChanged = useMemo(() => {
		return (
			JSON.stringify(tempSelectedVehicles) !==
				JSON.stringify(selectedVehicles) ||
			JSON.stringify(tempSelectedVffProgramm) !==
				JSON.stringify(selectedVffProgramm) ||
			JSON.stringify(tempSelectedVffPartNo) !==
				JSON.stringify(selectedVffPartNo) ||
			JSON.stringify(tempSelectedVffPurchaseType) !==
				JSON.stringify(selectedVffPurchaseType)
		);
	}, [
		tempSelectedVehicles,
		selectedVehicles,
		tempSelectedVffProgramm,
		selectedVffProgramm,
		tempSelectedVffPartNo,
		selectedVffPartNo,
		tempSelectedVffPurchaseType,
		selectedVffPurchaseType,
	]);

	// Derived state for filtered vehicle numbers based on selected programm
	const filteredVehicleNumbers = useMemo(() => {
		if (
			tempSelectedVffProgramm.includes("all") ||
			tempSelectedVffProgramm.length === 0
		) {
			return vehicleNumbers;
		}
		// If multiple programms are selected, combine their vehicle numbers
		const allVehicles = new Set();
		tempSelectedVffProgramm.forEach((programm) => {
			if (programmVehicleMapping[programm]) {
				programmVehicleMapping[programm].forEach((vehicle) =>
					allVehicles.add(vehicle),
				);
			}
		});
		return Array.from(allVehicles);
	}, [tempSelectedVffProgramm, programmVehicleMapping, vehicleNumbers]);

	// Process VFF dashboard data - Define this first to avoid initialization error
	const processVffData = useCallback(
		(
			vffData,
			filterProgramm = selectedVffProgramm,
			filterVehicles = selectedVehicles,
		) => {
			console.log("🔍 processVffData called with:", {
				vffDataLength: vffData?.length,
				filterProgramm,
				filterVehicles,
			});

			if (!vffData || vffData.length === 0) {
				setError("No VFF data available");
				setVffChartData(null);
				setVffTotalVehicles(0);
				setVffTotalAnflugTypes(0);
				setVffTotalParts(0);
				setAnflugTypes([]);
				setVehicleNumbers([]);
				return;
			}

			// Handle the new data structure from the updated backend query
			let processedData = [];

			if (Array.isArray(vffData) && vffData.length > 0) {
				// Check if we have the new status_color data structure
				if (vffData[0] && vffData[0].status_stacks) {
					console.log("✅ Using new status_color data format");
					processedData = vffData;
				} else if (vffData[0] && vffData[0].vehicle_no && vffData[0].date) {
					console.log("✅ Using structured data format");
					processedData = vffData;
				} else {
					console.log("⚠️ Converting old format to new status_color format");
					const vehicleData = {};
					vffData.forEach((item) => {
						const vehicleNo = item.produktnr || "Unknown";
						const statusColor = item.status_color || "Unknown";
						const date = item.bedarfstermin_date
							? item.bedarfstermin_date.split(" ")[0]
							: "N/A";
						const count = item.cnt1 || 0;
						const total = item.Total || 0;
						const buildDate = item.build_date
							? String(item.build_date).split(" ")[0]
							: null;
						const vehicleDateKey = `${vehicleNo}_${date}`;

						if (!vehicleData[vehicleDateKey]) {
							vehicleData[vehicleDateKey] = {
								vehicle_no: vehicleNo,
								date: date,
								build_date: buildDate,
								vehicle_date_id: vehicleDateKey,
								status_stacks: {},
								stacks: {}, // Keep for backward compatibility
								total_count: total,
								available_count: 0,
								t730_programm: item.t730_programm || "Unknown",
								programms: item.t730_programm ? [item.t730_programm] : [],
							};
						}

						// Add to status_stacks
						vehicleData[vehicleDateKey].status_stacks[statusColor] =
							(vehicleData[vehicleDateKey].status_stacks[statusColor] || 0) +
							count;
						vehicleData[vehicleDateKey].stacks[statusColor] =
							(vehicleData[vehicleDateKey].stacks[statusColor] || 0) + count;

						// Count available parts
						if (statusColor === "available") {
							vehicleData[vehicleDateKey].available_count += count;
						}

						// Add programm if not already present
						if (
							item.t730_programm &&
							!vehicleData[vehicleDateKey].programms.includes(
								item.t730_programm,
							)
						) {
							vehicleData[vehicleDateKey].programms.push(item.t730_programm);
						}
					});
					processedData = Object.values(vehicleData);
				}
			}

			console.log("📊 Processed data sample:", processedData[0]);

			// Apply programm filter (vehicle matched if any of its programms in selection)
			let filteredProcessedData = processedData;
			if (!filterProgramm.includes("all") && filterProgramm.length > 0) {
				console.log(
					"🔍 Applying programm filter (multi-programm aware):",
					filterProgramm,
				);
				filteredProcessedData = processedData.filter((vehicleDateCombo) => {
					const primary = vehicleDateCombo.t730_programm;
					const allProgramms =
						vehicleDateCombo.programms || (primary ? [primary] : []);
					const match = allProgramms.some((p) => filterProgramm.includes(p));
					if (!match) {
						console.log(
							`Skipping vehicle-date combo ${vehicleDateCombo.vehicle_date_id || vehicleDateCombo.vehicle_no}; programms=${allProgramms}`,
						);
					}
					return match;
				});
				console.log(
					`Filtered from ${processedData.length} to ${filteredProcessedData.length} vehicle-date combinations`,
				);
			}

			// Extract all unique status colors from filtered data (using both status_stacks and stacks for compatibility)
			const allStatusColors = new Set();
			filteredProcessedData.forEach((vehicleDateCombo) => {
				const stacks =
					vehicleDateCombo.status_stacks || vehicleDateCombo.stacks || {};
				Object.keys(stacks).forEach((statusColor) => {
					allStatusColors.add(statusColor);
				});
			});
			const statusColorTypes = Array.from(allStatusColors).sort();

			// Get unique vehicle numbers for the filter dropdown (not vehicle-date combinations)
			const uniqueVehicles = [
				...new Set(filteredProcessedData.map((item) => item.vehicle_no)),
			].sort();

			// Sort filtered data by date first, then by total count (descending) for display
			const sortedFilteredData = filteredProcessedData.sort((a, b) => {
				// Primary sort by date
				if (a.date !== b.date) {
					return a.date.localeCompare(b.date);
				}
				// Secondary sort by total count (descending)
				return b.total_count - a.total_count;
			});

			// Calculate totals from filtered data
			const totalVehicleDateCombinations = filteredProcessedData.length;
			const totalUniqueVehicles = uniqueVehicles.length;
			const totalStatusTypes = statusColorTypes.length;
			const vffTotalPartsFiltered = filteredProcessedData.reduce(
				(sum, vehicleDateCombo) => sum + vehicleDateCombo.total_count,
				0,
			);

			console.log("📈 VFF Dashboard stats (status_color format):", {
				totalVehicleDateCombinations,
				totalUniqueVehicles,
				totalStatusTypes,
				totalParts: vffTotalPartsFiltered,
				statusColorTypes,
			});

			setAnflugTypes(statusColorTypes); // Reuse existing state variable for status colors
			setVehicleNumbers(uniqueVehicles);
			setVffTotalVehicles(totalUniqueVehicles);
			setVffTotalAnflugTypes(totalStatusTypes);
			setVffTotalParts(vffTotalPartsFiltered); // Apply vehicle filter - filter by vehicle number, but keep all dates for selected vehicles
			const filteredVehicleDateCombinations =
				filterVehicles.includes("all") || filterVehicles.length === 0
					? sortedFilteredData
					: sortedFilteredData.filter((combo) =>
							filterVehicles.includes(combo.vehicle_no),
						);

			console.log("🚗 Vehicle filter applied:", {
				originalCombinations: sortedFilteredData.length,
				filteredCombinations: filteredVehicleDateCombinations.length,
				filterVehicles,
			}); // Create chart data structure for the new table-style display
			const chartData = {
				data: filteredVehicleDateCombinations,
				labels: filteredVehicleDateCombinations.map((combo) => {
					// keep existing label pattern for timeline; for vehicle-only aggregated items date may be null
					return `${combo.vehicle_no}\n${combo.date || ""}`;
				}),
				datasets: statusColorTypes.map((statusColor) => {
					const color = getVffColor(statusColor);
					return {
						label: statusColor,
						data: filteredVehicleDateCombinations.map((combo) => {
							const stacks = combo.status_stacks || combo.stacks || {};
							return stacks[statusColor] || 0;
						}),
						backgroundColor: color.bg,
						borderColor: color.border,
						borderWidth: 1,
					};
				}),
			};

			setVffChartData(chartData);
			if (error) setError(null);
		},
		[selectedVffProgramm, selectedVehicles, error],
	);

	// Process main dashboard data
	const processMainData = useCallback(
		(
			rawData,
			filterOpts,
			teams = selectedTeams,
			project = selectedProject,
			programm = selectedProgramm,
			purchaseType = selectedPurchaseType,
		) => {
			// rawData now represents aggregated rows: [{ se_team, bezugsart, part_count }]
			if (!rawData || !filterOpts) return;

			const purchaseTypes = {};
			const seTeams = {};
			const combinedData = {}; // Initialize with all possible values from filter options
			// Use purchaseTypes (BY SET = bezugsart) not purchaseTypesSystem (BY SYSTEM = bezugsart_sys_m)
			(filterOpts.purchaseTypes || []).forEach((type) => {
				purchaseTypes[type] = 0;
			});
			(filterOpts.seTeams || []).forEach((team) => {
				seTeams[team] = 0;
			});
			(filterOpts.seTeams || []).forEach((team) => {
				combinedData[team] = {};
				(filterOpts.purchaseTypes || []).forEach((type) => {
					combinedData[team][type] = 0;
				});
			});

			// Aggregated data: increment using part_count directly
			rawData.forEach((item) => {
				const purchaseType = item.bezugsart || "Unknown";
				const seTeam = item.se_team || "Unknown";
				const count = item.part_count || 0;

				// Only count if the team is in the filter or 'all' is selected
				if (teams.includes("all") || teams.includes(seTeam)) {
					purchaseTypes[purchaseType] =
						(purchaseTypes[purchaseType] || 0) + count;
					seTeams[seTeam] = (seTeams[seTeam] || 0) + count;

					if (!combinedData[seTeam]) combinedData[seTeam] = {};
					combinedData[seTeam][purchaseType] =
						(combinedData[seTeam][purchaseType] || 0) + count;
				}
			});

			// Filter out zero counts for the cards
			const filteredPurchaseTypes = Object.fromEntries(
				Object.entries(purchaseTypes).filter(([_, count]) => count > 0),
			);
			const filteredSeTeams = Object.fromEntries(
				Object.entries(seTeams).filter(([_, count]) => count > 0),
			);

			setPurchaseTypeCounts(filteredPurchaseTypes);
			setSeTeamCounts(filteredSeTeams);
			setCombinedData(combinedData);

			// Calculate totals per team
			const seTeamTotals = Object.entries(combinedData)
				.map(([team, types]) => ({
					team,
					total: Object.values(types).reduce((sum, count) => sum + count, 0),
				}))
				.sort((a, b) => b.total - a.total);

			const allSeTeams = seTeamTotals.map((item) => item.team);
			const purchaseTypeTotals = {};
			Object.values(combinedData).forEach((types) => {
				Object.entries(types).forEach(([type, count]) => {
					purchaseTypeTotals[type] = (purchaseTypeTotals[type] || 0) + count;
				});
			});

			const topPurchaseTypes = Object.entries(purchaseTypeTotals)
				.sort((a, b) => b[1] - a[1])
				.slice(0, 5)
				.map((item) => item[0]);

			const PURCHASE_TYPE_COLORS = {
				M: "#545454", // Dark grey - Assembly at MAN
				K: "#6b9700", // Green - Purchase Part
				H: "#ff8c00", // Orange - Inhouse Part
				"-": "#cc0000", // Red
			};
			const fallbackColors = [
				"#1e2832",
				"#4489c5",
				"#ffa500",
				"#303c49",
				"#858585",
			];

			// Apply project filtering first
			let filteredTeams = allSeTeams;
			if (!project.includes("all") && filterOpts.projectTeamMapping) {
				const projectTeams = [
					...new Set(
						project.flatMap(
							(proj) => filterOpts.projectTeamMapping[proj] || [],
						),
					),
				];
				filteredTeams = filteredTeams.filter((team) =>
					projectTeams.includes(team),
				);
			}
			if (!teams.includes("all") && teams.length > 0) {
				filteredTeams = filteredTeams.filter((team) => teams.includes(team));
			}

			const displayTeams = filteredTeams.filter((team) => {
				const teamTotal = Object.values(combinedData[team] || {}).reduce(
					(sum, count) => sum + count,
					0,
				);
				return teamTotal > 0;
			});
			let fallbackIndex = 0;
			const datasets = topPurchaseTypes.map((type) => {
				const color =
					PURCHASE_TYPE_COLORS[type] ||
					fallbackColors[fallbackIndex++ % fallbackColors.length];
				return {
					label: type,
					data: displayTeams.map((team) => combinedData[team]?.[type] || 0),
					backgroundColor: color,
					borderColor: color,
					borderWidth: 1,
				};
			});

			setMainChartData({ labels: displayTeams, datasets });
		},
		[selectedTeams, selectedProject, selectedProgramm, selectedPurchaseType],
	);

	// Fetch VFF dashboard data
	const fetchVffData = useCallback(async () => {
		// Prevent duplicate initial data loads
		if (initialFetchCompletedRef.current.vff) {
			console.log(
				"Initial VFF data already fetched, skipping duplicate load...",
			);
			return;
		}

		// Prevent duplicate requests
		if (requestInProgressRef.current.vffData) {
			console.log("VFF data request already in progress, skipping...");
			return;
		}

		try {
			requestInProgressRef.current.vffData = true;
			setLoading(true);
			setError(null);
			setMainChartData(null); // Build query parameters for filters
			const params = new URLSearchParams();

			// Add part number filter
			if (!selectedVffPartNo.includes("all") && selectedVffPartNo.length > 0) {
				params.append("part_no_filter", selectedVffPartNo.join(","));
			}

			// Add purchase type filter
			if (
				!selectedVffPurchaseType.includes("all") &&
				selectedVffPurchaseType.length > 0
			) {
				params.append(
					"purchase_type_filter",
					selectedVffPurchaseType.join(","),
				);
			}

			const queryString = params.toString();
			const url = queryString
				? `/api/dashboard-vff-data?${queryString}`
				: "/api/dashboard-vff-data";

			const response = await apiClient.get(url);
			const result = response.data;
			setVffData(result.data || []);
			setProgramms(result.programms || []);
			setProgrammVehicleMapping(result.programm_vehicle_mapping || {});
			processVffData(result.data);

			// Mark initial fetch as completed to prevent duplicate loads
			initialFetchCompletedRef.current.vff = true;

			setLoading(false);
		} catch (err) {
			console.error("Error fetching VFF dashboard data:", err);
			setError(err.message);
			setLoading(false);
		} finally {
			requestInProgressRef.current.vffData = false;
		}
	}, [selectedVffPartNo, selectedVffPurchaseType, processVffData]);

	// Fetch VFF filter options
	const fetchVffFilterOptions = useCallback(async () => {
		try {
			console.log("🔍 Fetching VFF filter options...");
			const response = await apiClient.get("/api/dashboard-vff-filter-options");
			const result = response.data;

			setVffPartNumbers(result.part_numbers || []);
			setVffPurchaseTypes(result.purchase_types || []);
			setVffFilterOptions(result);

			console.log("✅ VFF filter options loaded:", result);
			console.log("🔍 Part numbers available:", result.part_numbers);
			console.log("🔍 Purchase types available:", result.purchase_types);
		} catch (error) {
			console.error("Error fetching VFF filter options:", error);
			setError("Failed to load VFF filter options");
		}
	}, []);

	// Fetch main dashboard data
	const fetchMainData = useCallback(async () => {
		if (initialFetchCompletedRef.current.main) {
			console.log(
				"Initial main data already fetched, skipping duplicate load...",
			);
			return;
		}
		if (requestInProgressRef.current.mainData) {
			console.log("Main data request already in progress, skipping...");
			return;
		}
		try {
			requestInProgressRef.current.mainData = true;
			setLoading(true);
			setError(null);
			setMainChartData(null);
			const response = await apiClient.post(
				"/api/dashboard-filtered-data?include_filters=true",
				{
					project: selectedProject.includes("all") ? null : selectedProject,
					programm: selectedProgramm.includes("all") ? null : selectedProgramm,
					purchase_type: selectedPurchaseType.includes("all")
						? null
						: selectedPurchaseType,
					se_teams: selectedTeams.includes("all") ? null : selectedTeams,
					category: selectedCategory.includes("all") ? null : selectedCategory,
					items_per_page: 9999999, // legacy param (ignored by backend aggregation)
				},
			);
			const result = response.data;

			setFilterOptions(result.filterOptions || {});
			setMainData(result.data || []); // aggregated rows retained for potential debugging
			setTotalParts(result.total_parts || 0);
			processMainData(
				result.data || [],
				result.filterOptions || {},
				selectedTeams,
				selectedProject,
				selectedProgramm,
				selectedPurchaseType,
			);

			initialFetchCompletedRef.current.main = true;
			setLoading(false);
		} catch (err) {
			console.error("Error fetching main dashboard data:", err);
			setError(err.message);
			setLoading(false);
		} finally {
			requestInProgressRef.current.mainData = false;
		}
	}, [
		selectedProject,
		selectedTeams,
		selectedProgramm,
		selectedPurchaseType,
		processMainData,
	]);

	// Apply filters for main dashboard
	const handleApplyFilters = useCallback(async () => {
		if (!hasFiltersChanged) return;
		if (requestInProgressRef.current.mainData) {
			console.log("Filter request already in progress, skipping...");
			return;
		}
		setIsChartLoading(true);
		setSelectedProject(tempSelectedProject);
		setSelectedTeams(tempSelectedTeams);
		setSelectedProgramm(tempSelectedProgramm);
		setSelectedPurchaseType(tempSelectedPurchaseType);
		setSelectedCategory(tempSelectedCategory);
		try {
			requestInProgressRef.current.mainData = true;
			const response = await apiClient.post("/api/dashboard-filtered-data", {
				project: tempSelectedProject.includes("all")
					? null
					: tempSelectedProject,
				programm: tempSelectedProgramm.includes("all")
					? null
					: tempSelectedProgramm,
				purchase_type: tempSelectedPurchaseType.includes("all")
					? null
					: tempSelectedPurchaseType,
				se_teams: tempSelectedTeams.includes("all") ? null : tempSelectedTeams,
				category: tempSelectedCategory.includes("all")
					? null
					: tempSelectedCategory,
				items_per_page: 9999999,
			});
			const result = response.data;
			setMainData(result.data || []);
			setTotalParts(result.total_parts || 0);
			processMainData(
				result.data || [],
				filterOptions,
				tempSelectedTeams,
				tempSelectedProject,
				tempSelectedProgramm,
				tempSelectedPurchaseType,
			);
		} catch (err) {
			console.error("Error applying filters:", err);
			setError(err.message);
		} finally {
			requestInProgressRef.current.mainData = false;
			setIsChartLoading(false);
		}
	}, [
		hasFiltersChanged,
		tempSelectedProject,
		tempSelectedTeams,
		tempSelectedProgramm,
		tempSelectedPurchaseType,
		tempSelectedCategory,
		filterOptions,
		processMainData,
	]);

	// Reset filters to default state and fetch fresh data
	const handleResetFilters = useCallback(async () => {
		if (requestInProgressRef.current.mainData) {
			console.log("Reset filters request already in progress, skipping...");
			return;
		}
		setTempSelectedTeams(["all"]);
		setTempSelectedProject(["all"]);
		setTempSelectedProgramm(["all"]);
		setTempSelectedPurchaseType(["all"]);
		setTempSelectedCategory(["all"]);
		setSelectedTeams(["all"]);
		setSelectedProject(["all"]);
		setSelectedProgramm(["all"]);
		setSelectedPurchaseType(["all"]);
		setSelectedCategory(["all"]);
		setIsChartLoading(true);
		try {
			requestInProgressRef.current.mainData = true;
			const response = await apiClient.post("/api/dashboard-filtered-data", {
				project: null,
				programm: null,
				purchase_type: null,
				se_teams: null,
				category: null,
				items_per_page: 9999999,
			});
			const result = response.data;
			setMainData(result.data || []);
			setTotalParts(result.total_parts || 0);
			processMainData(
				result.data || [],
				filterOptions,
				["all"],
				["all"],
				["all"],
				["all"],
			);
		} catch (err) {
			console.error("Error resetting filters:", err);
			setError(err.message);
		} finally {
			requestInProgressRef.current.mainData = false;
			setIsChartLoading(false);
		}
	}, [filterOptions, processMainData]); // Apply filters for VFF dashboard
	const handleApplyVffFilters = useCallback(async () => {
		console.log("🔧 handleApplyVffFilters called:", {
			hasVffFiltersChanged,
			tempSelectedVffProgramm,
			tempSelectedVehicles,
			tempSelectedVffPartNo,
			tempSelectedVffPurchaseType,
		});

		if (!hasVffFiltersChanged) return;

		setIsChartLoading(true);

		// Update the filter states
		setSelectedVffProgramm(tempSelectedVffProgramm);
		setSelectedVehicles(tempSelectedVehicles);
		setSelectedVffPartNo(tempSelectedVffPartNo);
		setSelectedVffPurchaseType(tempSelectedVffPurchaseType);

		// Use setTimeout to ensure state updates are applied before fetching data
		setTimeout(async () => {
			try {
				// Reset the initial fetch flag to allow fresh data fetching
				initialFetchCompletedRef.current.vff = false;

				// Manually build query parameters using temp values since state may not be updated yet
				const params = new URLSearchParams();
				// Add programm filter
				if (
					!tempSelectedVffProgramm.includes("all") &&
					tempSelectedVffProgramm.length > 0
				) {
					params.append("programm_filter", tempSelectedVffProgramm.join(","));
					console.log(
						"🔍 Applied programm_filter:",
						tempSelectedVffProgramm.join(","),
					);
				}

				// Add vehicle filter
				if (
					!tempSelectedVehicles.includes("all") &&
					tempSelectedVehicles.length > 0
				) {
					params.append("vehicle_filter", tempSelectedVehicles.join(","));
					console.log(
						"🔍 Applied vehicle_filter:",
						tempSelectedVehicles.join(","),
					);
				}

				// Add part number filter
				if (
					!tempSelectedVffPartNo.includes("all") &&
					tempSelectedVffPartNo.length > 0
				) {
					params.append("part_no_filter", tempSelectedVffPartNo.join(","));
					console.log(
						"🔍 Applied part_no_filter:",
						tempSelectedVffPartNo.join(","),
					);
				}

				// Add purchase type filter
				if (
					!tempSelectedVffPurchaseType.includes("all") &&
					tempSelectedVffPurchaseType.length > 0
				) {
					params.append(
						"purchase_type_filter",
						tempSelectedVffPurchaseType.join(","),
					);
					console.log(
						"🔍 Applied purchase_type_filter:",
						tempSelectedVffPurchaseType.join(","),
					);
				}

				const queryString = params.toString();
				const url = queryString
					? `/api/dashboard-vff-data?${queryString}`
					: "/api/dashboard-vff-data";

				console.log("🌐 Fetching VFF data with URL:", url);

				setError(null);
				const response = await apiClient.get(url);
				const result = response.data;

				console.log("✅ VFF data fetched successfully:", {
					dataCount: result.data?.length || 0,
					programmsCount: result.programms?.length || 0,
				});

				setVffData(result.data || []);
				setProgramms(result.programms || []);
				setProgrammVehicleMapping(result.programm_vehicle_mapping || {});
				processVffData(result.data);

				setIsChartLoading(false);
			} catch (err) {
				console.error("❌ Error fetching filtered VFF data:", err);
				setError(err.message);
				setIsChartLoading(false);
			}
		}, 50);
	}, [
		hasVffFiltersChanged,
		tempSelectedVffProgramm,
		tempSelectedVehicles,
		tempSelectedVffPartNo,
		tempSelectedVffPurchaseType,
		processVffData,
	]);
	// Reset VFF filters
	const handleResetVffFilters = useCallback(() => {
		setTempSelectedVffProgramm(["all"]);
		setTempSelectedVehicles(["all"]);
		setTempSelectedVffPartNo(["all"]);
		setTempSelectedVffPurchaseType(["all"]);
		setSelectedVffProgramm(["all"]);
		setSelectedVehicles(["all"]);
		setSelectedVffPartNo(["all"]);
		setSelectedVffPurchaseType(["all"]);

		// Process VFF data with reset filters
		setTimeout(() => {
			processVffData(vffData, ["all"], ["all"]);
			setIsChartLoading(false);
		}, 100);
	}, [vffData, processVffData]);

	// Handle team selection change (temporary)
	const handleTeamChange = useCallback(
		(event) => {
			const { value } = event.target;
			if (value.includes("all") && !tempSelectedTeams.includes("all")) {
				setTempSelectedTeams(["all"]);
				return;
			}
			if (value.includes("all") && tempSelectedTeams.length > 1) {
				setTempSelectedTeams(["all"]);
				return;
			}
			if (tempSelectedTeams.includes("all") && value.length > 0) {
				const newSelection = value.filter((v) => v !== "all");
				setTempSelectedTeams(newSelection.length > 0 ? newSelection : ["all"]);
				return;
			}
			setTempSelectedTeams(value.length > 0 ? value : ["all"]);
		},
		[tempSelectedTeams],
	);

	// Handle project selection change (temporary)
	const handleProjectChange = useCallback(
		(event) => {
			const { value } = event.target;
			if (value.includes("all") && !tempSelectedProject.includes("all")) {
				setTempSelectedProject(["all"]);
				setTempSelectedTeams(["all"]);
				return;
			}
			if (value.includes("all") && tempSelectedProject.length > 1) {
				setTempSelectedProject(["all"]);
				setTempSelectedTeams(["all"]);
				return;
			}
			if (tempSelectedProject.includes("all") && value.length > 0) {
				const newSelection = value.filter((v) => v !== "all");
				setTempSelectedProject(
					newSelection.length > 0 ? newSelection : ["all"],
				);
				setTempSelectedTeams(["all"]);
				return;
			}
			// When specific projects are selected, filter teams to only include those that belong to the selected projects
			const newSelectedProjects = value.length > 0 ? value : ["all"];
			setTempSelectedProject(newSelectedProjects);
			// If "all" projects selected, reset to all teams
			if (newSelectedProjects.includes("all")) {
				setTempSelectedTeams(["all"]);
			} else {
				// Filter current team selection to only include teams that belong to the newly selected projects
				const availableTeamsForSelectedProjects =
					filterOptions?.projectTeamMapping
						? [
								...new Set(
									newSelectedProjects.flatMap(
										(project) =>
											filterOptions.projectTeamMapping[project] || [],
									),
								),
							]
						: [];
				// Keep only the teams that are still valid for the selected projects
				const validTeams = tempSelectedTeams.filter(
					(team) =>
						team === "all" || availableTeamsForSelectedProjects.includes(team),
				);
				// If no valid teams remain or only 'all' was selected, reset to 'all'
				// Otherwise, keep the valid team selection
				if (
					validTeams.length === 0 ||
					(validTeams.length === 1 && validTeams[0] === "all")
				) {
					setTempSelectedTeams(["all"]);
				} else {
					setTempSelectedTeams(validTeams);
				}
			}
		},
		[tempSelectedProject, tempSelectedTeams, filterOptions],
	);

	// Only reset tempSelectedProject and tempSelectedTeams when the applied filters or filterOptions change
	useEffect(() => {
		setTempSelectedProject(selectedProject);
		setTempSelectedTeams(selectedTeams);
	}, [selectedProject, selectedTeams, filterOptions]);

	// Handle programm selection change (temporary)
	const handleProgrammChange = useCallback(
		(event) => {
			const { value } = event.target;

			if (value.includes("all") && !tempSelectedProgramm.includes("all")) {
				setTempSelectedProgramm(["all"]);
				return;
			}

			if (value.includes("all") && tempSelectedProgramm.length > 1) {
				setTempSelectedProgramm(["all"]);
				return;
			}

			if (tempSelectedProgramm.includes("all") && value.length > 0) {
				const newSelection = value.filter((v) => v !== "all");
				setTempSelectedProgramm(
					newSelection.length > 0 ? newSelection : ["all"],
				);
				return;
			}

			setTempSelectedProgramm(value.length > 0 ? value : ["all"]);
		},
		[tempSelectedProgramm],
	);

	// Handle purchase type selection change (temporary)
	const handlePurchaseTypeChange = useCallback(
		(event) => {
			const { value } = event.target;

			if (value.includes("all") && !tempSelectedPurchaseType.includes("all")) {
				setTempSelectedPurchaseType(["all"]);
				return;
			}

			if (value.includes("all") && tempSelectedPurchaseType.length > 1) {
				setTempSelectedPurchaseType(["all"]);
				return;
			}

			if (tempSelectedPurchaseType.includes("all") && value.length > 0) {
				const newSelection = value.filter((v) => v !== "all");
				setTempSelectedPurchaseType(
					newSelection.length > 0 ? newSelection : ["all"],
				);
				return;
			}
			setTempSelectedPurchaseType(value.length > 0 ? value : ["all"]);
		},
		[tempSelectedPurchaseType],
	);

	// Handle category selection change (temporary)
	const handleCategoryChange = useCallback(
		(event) => {
			const { value } = event.target;

			if (value.includes("all") && !tempSelectedCategory.includes("all")) {
				setTempSelectedCategory(["all"]);
				return;
			}
			if (value.includes("all") && tempSelectedCategory.length > 1) {
				setTempSelectedCategory(["all"]);
				return;
			}
			if (tempSelectedCategory.includes("all") && value.length > 0) {
				const newSelection = value.filter((v) => v !== "all");
				setTempSelectedCategory(
					newSelection.length > 0 ? newSelection : ["all"],
				);
				return;
			}
			setTempSelectedCategory(value.length > 0 ? value : ["all"]);
		},
		[tempSelectedCategory],
	);

	// Handle page change
	const handlePageChange = useCallback((event) => {
		const newPage = event.target.value;

		// Cancel any ongoing requests when switching pages
		requestInProgressRef.current = {
			mainData: false,
			vffData: false,
			filterOptions: false,
		};

		setSelectedPage(newPage);
		setError(null);
		setIsChartLoading(false);
		setSelectedAnflug("all");
		setSelectedVehicles(["all"]);
		setSelectedProject(["all"]);
		setSelectedTeams(["all"]);
		setSelectedProgramm(["all"]);
		setSelectedPurchaseType(["all"]);
		setSelectedCategory(["all"]);
		setSelectedVffProgramm(["all"]);
		setSelectedVffPartNo(["all"]);
		setSelectedVffPurchaseType(["all"]);
		setTempSelectedAnflug("all");
		setTempSelectedVehicles(["all"]);
		setTempSelectedProject(["all"]);
		setTempSelectedTeams(["all"]);
		setTempSelectedProgramm(["all"]);
		setTempSelectedPurchaseType(["all"]);
		setTempSelectedCategory(["all"]);
		setTempSelectedVffProgramm(["all"]);
		setTempSelectedVffPartNo(["all"]);
		setTempSelectedVffPurchaseType(["all"]);

		// Make sure we don't reset the initialFetchCompletedRef for the current page
		// This prevents duplicate data fetching when switching between pages
		if (newPage === "vff") {
			// Only reset main page flag, keep vff flag as is
			initialFetchCompletedRef.current = {
				...initialFetchCompletedRef.current,
				main: false,
			};
			fetchVffData();
			setMainChartData(null);
		} else if (newPage === "series") {
			// Series dashboard handles its own data fetching
			setMainChartData(null);
			setVffChartData(null);
		} else {
			// Only reset vff page flag, keep main flag as is
			initialFetchCompletedRef.current = {
				...initialFetchCompletedRef.current,
				vff: false,
			};
			fetchMainData();
			setVffChartData(null);
		}
	}, []); // Remove fetchMainData and fetchVffData from dependencies to prevent cycles

	// Handle anflug type filter change (temporary)
	const handleAnflugChange = useCallback((event) => {
		setTempSelectedAnflug(event.target.value);
	}, []); // Handle VFF programm filter change (temporary)
	const handleVffProgrammChange = useCallback(
		(event) => {
			console.log("🔧🔧🔧 handleVffProgrammChange TRIGGERED!", event);
			const value = event.target.value;
			console.log("🔧 handleVffProgrammChange called:", {
				value,
				tempSelectedVffProgramm,
				valueType: typeof value,
			});

			// Handle the case where value might be a string instead of array
			const valueArray = Array.isArray(value) ? value : [value];
			console.log("🔧 valueArray:", valueArray);

			// Check if user clicked "all" - if so, select only "all"
			if (
				valueArray.includes("all") &&
				!tempSelectedVffProgramm.includes("all")
			) {
				console.log('🔧 User clicked "all", setting to [all]');
				setTempSelectedVffProgramm(["all"]);
				setTempSelectedVehicles(["all"]);
			} else if (
				valueArray.includes("all") &&
				tempSelectedVffProgramm.includes("all") &&
				valueArray.length > 1
			) {
				// User had "all" selected and clicked on specific item - remove "all" and keep only the specific item
				const newSelection = valueArray.filter((v) => v !== "all");
				console.log(
					'🔧 User clicked specific item while "all" was selected, setting to:',
					newSelection,
				);
				setTempSelectedVffProgramm(newSelection);
				setTempSelectedVehicles(["all"]);
			} else if (valueArray.length === 0) {
				console.log("🔧 Empty selection, defaulting to [all]");
				setTempSelectedVffProgramm(["all"]);
				setTempSelectedVehicles(["all"]);
			} else {
				// Normal case - filter out "all" and use the specific selections
				const newSelection = valueArray.filter((v) => v !== "all");
				console.log('🔧 newSelection after filtering out "all":', newSelection);
				const finalSelection = newSelection.length > 0 ? newSelection : ["all"];
				console.log("🔧 Setting tempSelectedVffProgramm to:", finalSelection);
				setTempSelectedVffProgramm(finalSelection);
				setTempSelectedVehicles(["all"]);
			}
		},
		[tempSelectedVffProgramm],
	); // Handle vehicle number filter change (temporary)
	const handleVehicleChange = useCallback(
		(event) => {
			console.log("🔧🔧🔧 handleVehicleChange TRIGGERED!", event);
			const value = event.target.value;
			console.log("🔧 handleVehicleChange called:", {
				value,
				tempSelectedVehicles,
				valueType: typeof value,
			});

			// Handle the case where value might be a string instead of array
			const valueArray = Array.isArray(value) ? value : [value];
			console.log("🔧 valueArray:", valueArray);

			// Check if user clicked "all" - if so, select only "all"
			if (valueArray.includes("all") && !tempSelectedVehicles.includes("all")) {
				console.log('🔧 User clicked "all", setting to [all]');
				setTempSelectedVehicles(["all"]);
			} else if (
				valueArray.includes("all") &&
				tempSelectedVehicles.includes("all") &&
				valueArray.length > 1
			) {
				// User had "all" selected and clicked on specific item - remove "all" and keep only the specific item
				const newSelection = valueArray.filter((v) => v !== "all");
				console.log(
					'🔧 User clicked specific item while "all" was selected, setting to:',
					newSelection,
				);
				setTempSelectedVehicles(newSelection);
			} else if (valueArray.length === 0) {
				console.log("🔧 Empty selection, defaulting to [all]");
				setTempSelectedVehicles(["all"]);
			} else {
				// Normal case - filter out "all" and use the specific selections
				const newSelection = valueArray.filter((v) => v !== "all");
				console.log('🔧 newSelection after filtering out "all":', newSelection);
				const finalSelection = newSelection.length > 0 ? newSelection : ["all"];
				console.log("🔧 Setting tempSelectedVehicles to:", finalSelection);
				setTempSelectedVehicles(finalSelection);
			}
		},
		[tempSelectedVehicles],
	);
	// Handle VFF part number filter change (temporary)
	const handleVffPartNoChange = useCallback(
		(event) => {
			console.log("🔧🔧🔧 handleVffPartNoChange TRIGGERED!", event);
			const value = event.target.value;
			console.log("🔧 handleVffPartNoChange called:", {
				value,
				tempSelectedVffPartNo,
				valueType: typeof value,
			});

			// Handle the case where value might be a string instead of array
			const valueArray = Array.isArray(value) ? value : [value];
			console.log("🔧 valueArray:", valueArray);
			// Check if user clicked "all" - if so, select only "all"
			if (
				valueArray.includes("all") &&
				!tempSelectedVffPartNo.includes("all")
			) {
				console.log('🔧 User clicked "all", setting to [all]');
				setTempSelectedVffPartNo(["all"]);
			} else if (
				valueArray.includes("all") &&
				tempSelectedVffPartNo.includes("all") &&
				valueArray.length > 1
			) {
				// User had "all" selected and clicked on specific item - remove "all" and keep only the specific item
				const newSelection = valueArray.filter((v) => v !== "all");
				console.log(
					'🔧 User clicked specific item while "all" was selected, setting to:',
					newSelection,
				);
				setTempSelectedVffPartNo(newSelection);
			} else if (valueArray.length === 0) {
				console.log("🔧 Empty selection, defaulting to [all]");
				setTempSelectedVffPartNo(["all"]);
			} else {
				// Normal case - filter out "all" and use the specific selections
				const newSelection = valueArray.filter((v) => v !== "all");
				console.log('🔧 newSelection after filtering out "all":', newSelection);
				const finalSelection = newSelection.length > 0 ? newSelection : ["all"];
				console.log("🔧 Setting tempSelectedVffPartNo to:", finalSelection);
				setTempSelectedVffPartNo(finalSelection);
			}
		},
		[tempSelectedVffPartNo],
	);
	// Handle VFF purchase type filter change (temporary)
	const handleVffPurchaseTypeChange = useCallback(
		(event) => {
			console.log("🔧🔧🔧 handleVffPurchaseTypeChange TRIGGERED!", event);
			const value = event.target.value;
			console.log("🔧 handleVffPurchaseTypeChange called:", {
				value,
				tempSelectedVffPurchaseType,
				valueType: typeof value,
			});

			// Handle the case where value might be a string instead of array
			const valueArray = Array.isArray(value) ? value : [value];
			console.log("🔧 valueArray:", valueArray);

			// Check if user clicked "all" - if so, select only "all"
			if (
				valueArray.includes("all") &&
				!tempSelectedVffPurchaseType.includes("all")
			) {
				console.log('🔧 User clicked "all", setting to [all]');
				setTempSelectedVffPurchaseType(["all"]);
			} else if (
				valueArray.includes("all") &&
				tempSelectedVffPurchaseType.includes("all") &&
				valueArray.length > 1
			) {
				// User had "all" selected and clicked on specific item - remove "all" and keep only the specific item
				const newSelection = valueArray.filter((v) => v !== "all");
				console.log(
					'🔧 User clicked specific item while "all" was selected, setting to:',
					newSelection,
				);
				setTempSelectedVffPurchaseType(newSelection);
			} else if (valueArray.length === 0) {
				console.log("🔧 Empty selection, defaulting to [all]");
				setTempSelectedVffPurchaseType(["all"]);
			} else {
				// Normal case - filter out "all" and use the specific selections
				const newSelection = valueArray.filter((v) => v !== "all");
				console.log('🔧 newSelection after filtering out "all":', newSelection);
				const finalSelection = newSelection.length > 0 ? newSelection : ["all"];
				console.log(
					"🔧 Setting tempSelectedVffPurchaseType to:",
					finalSelection,
				);
				setTempSelectedVffPurchaseType(finalSelection);
			}
		},
		[tempSelectedVffPurchaseType],
	);
	// Export to PDF
	const exportToPDF = useCallback(async () => {
		const dashboardElement = document.getElementById("dashboard-container");
		if (!dashboardElement) {
			console.error("Dashboard container not found");
			return;
		}

		try {
			// Temporarily remove any transforms that might affect canvas rendering
			const originalStyle = dashboardElement.style.transform;
			dashboardElement.style.transform = "none";

			// Temporarily expand any scrollable VFF table containers so all rows are captured
			const scrollContainers = dashboardElement.querySelectorAll(
				'[data-vff-scroll-container="true"]',
			);
			const scrollContainerOriginalStyles = [];
			scrollContainers.forEach((el) => {
				scrollContainerOriginalStyles.push({
					maxHeight: el.style.maxHeight,
					overflowY: el.style.overflowY,
				});
				el.style.maxHeight = "none";
				el.style.overflowY = "visible";
			});

			// Create canvas with higher scale for better quality
			const canvas = await html2canvas(dashboardElement, {
				scale: 2,
				logging: false,
				useCORS: true,
				allowTaint: false,
				backgroundColor: "#f0f0f0",
				width: dashboardElement.scrollWidth,
				height: dashboardElement.scrollHeight,
				scrollX: 0,
				scrollY: 0,
				windowWidth: window.innerWidth,
				windowHeight: window.innerHeight,
			});

			// Restore original style
			dashboardElement.style.transform = originalStyle;

			// Restore scrollable container styles
			scrollContainers.forEach((el, i) => {
				el.style.maxHeight = scrollContainerOriginalStyles[i].maxHeight;
				el.style.overflowY = scrollContainerOriginalStyles[i].overflowY;
			});

			const imgData = canvas.toDataURL("image/png", 1.0);

			// Create PDF in landscape mode
			const pdf = new jsPDF({
				orientation: "landscape",
				unit: "mm",
				format: "a4",
			});

			const imgProps = pdf.getImageProperties(imgData);
			const pdfWidth = pdf.internal.pageSize.getWidth();
			const pdfHeight = pdf.internal.pageSize.getHeight();

			// Calculate dimensions to fit the page while maintaining aspect ratio
			const imgWidth = imgProps.width;
			const imgHeight = imgProps.height;
			const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

			const scaledWidth = imgWidth * ratio;
			const scaledHeight = imgHeight * ratio;

			// Center the image on the page
			const x = (pdfWidth - scaledWidth) / 2;
			const y = (pdfHeight - scaledHeight) / 2;

			// If the content is too tall, create multiple pages
			if (scaledHeight > pdfHeight) {
				let remainingHeight = scaledHeight;
				let sourceY = 0;
				let pageNum = 0;

				while (remainingHeight > 0) {
					if (pageNum > 0) {
						pdf.addPage();
					}

					const currentPageHeight = Math.min(remainingHeight, pdfHeight);
					const sourceHeight = currentPageHeight / ratio;

					// Create a temporary canvas for this page slice
					const tempCanvas = document.createElement("canvas");
					const tempCtx = tempCanvas.getContext("2d");
					tempCanvas.width = imgWidth;
					tempCanvas.height = sourceHeight;

					// Draw the slice
					tempCtx.drawImage(
						canvas,
						0,
						sourceY,
						imgWidth,
						sourceHeight,
						0,
						0,
						imgWidth,
						sourceHeight,
					);
					const tempImgData = tempCanvas.toDataURL("image/png", 1.0);

					pdf.addImage(
						tempImgData,
						"PNG",
						x,
						0,
						scaledWidth,
						currentPageHeight,
					);

					remainingHeight -= currentPageHeight;
					sourceY += sourceHeight;
					pageNum++;
				}
			} else {
				// Single page - center the content
				pdf.addImage(imgData, "PNG", x, y, scaledWidth, scaledHeight);
			}

			pdf.save("MAN_Parts_Tracking_Dashboard.pdf");
		} catch (err) {
			console.error("Error exporting PDF:", err);
			setError("Failed to export PDF");
		}
	}, []);

	// Initial data fetch
	useEffect(() => {
		// In Strict Mode, React will render components twice in development
		// We use the initialFetchCompletedRef to prevent duplicate requests
		if (selectedPage === "main") {
			// Only fetch if not already fetched for this page
			if (!initialFetchCompletedRef.current.main) {
				fetchMainData();
			}
		} else if (selectedPage === "vff") {
			// Only fetch if not already fetched for this page
			if (!initialFetchCompletedRef.current.vff) {
				fetchVffData();
			}
		} else if (selectedPage === "series") {
			// Series dashboard fetches its own data internally, so clear the global
			// loading flag; otherwise the page hangs on the loading spinner when
			// "series" is restored directly on refresh.
			setLoading(false);
		}
	}, [selectedPage, fetchMainData, fetchVffData]); // Include dependencies but they won't cause duplicates due to our flags
	// Initialize temporary filters when filters change
	useEffect(() => {
		setTempSelectedProject(selectedProject);
		setTempSelectedTeams(selectedTeams);
	}, [selectedProject, selectedTeams, filterOptions]);
	useEffect(() => {
		setTempSelectedAnflug(selectedAnflug);
		setTempSelectedVehicles(selectedVehicles);
		setTempSelectedVffProgramm(selectedVffProgramm);
		setTempSelectedVffPartNo(selectedVffPartNo);
		setTempSelectedVffPurchaseType(selectedVffPurchaseType);
	}, [
		selectedAnflug,
		selectedVehicles,
		selectedVffProgramm,
		selectedVffPartNo,
		selectedVffPurchaseType,
	]);

	// Fetch VFF filter options when VFF page is selected
	useEffect(() => {
		if (selectedPage === "vff" && !vffFilterOptions) {
			fetchVffFilterOptions();
		}
	}, [selectedPage, vffFilterOptions, fetchVffFilterOptions]);

	// Cleanup effect
	useEffect(() => {
		return () => {
			// Reset request flags when component unmounts
			requestInProgressRef.current = {
				mainData: false,
				vffData: false,
				filterOptions: false,
			};

			// Reset initial fetch completed flags
			initialFetchCompletedRef.current = {
				main: false,
				vff: false,
			};
		};
	}, []);

	if (loading && !mainChartData && !vffChartData) {
		return (
			<>
				<Header user={user} signOut={signOut} onStartTour={handleStartTour} />
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					height="70vh"
				>
					<CircularProgress size={60} thickness={4} sx={{ color: "#cc0000" }} />
					<Typography variant="body1" sx={{ mt: 2, color: "#666" }}>
						Loading dashboard data...
					</Typography>
				</Box>
			</>
		);
	}
	return (
		<div
			style={{
				backgroundColor: "#f0f0f0",
				height: "100vh",
				width: "100%",
				paddingTop: "calc(74px + var(--banner-height, 0px))",
				boxSizing: "border-box",
				overflowY: "auto",
				overflowX: "hidden",
			}}
		>
			<Header user={user} signOut={signOut} onStartTour={handleStartTour} />{" "}
			<VffDashboardJoyrideTour
				isOpen={isVffTourOpen}
				onClose={handleCloseTour}
			/>
			<SupplyTypeAllocationDashboardJoyrideTour
				isOpen={isStaTourOpen}
				onClose={handleCloseStaTour}
			/>
			<SeriesTrackingDashboardJoyrideTour
				isOpen={isSeriesTourOpen}
				onClose={handleCloseSeriesTour}
			/>
			<Box
				id="dashboard-container"
				sx={{
					backgroundColor: "#f0f0f0",
					minHeight: 0,
					fontFamily: "MAN EUROPE_Regular",
					margin: "0 auto",
					width: "100%",
					maxWidth: "100vw", // Ensure it doesn't exceed viewport width
					padding: "20px",
					overflowX: "hidden", // Prevent horizontal scrolling
					boxSizing: "border-box", // Include padding in width calculation
				}}
			>
				{/* Dashboard Type Selector */}
				<Box
					sx={{
						backgroundColor: "#fff",
						borderRadius: "8px",
						padding: "20px",
						marginBottom: "20px",
						boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							gap: 2,
							flexWrap: "wrap",
						}}
					>
						<Typography
							variant="h5"
							sx={{ fontFamily: "MAN EUROPE_Bold", color: "#1e2832" }}
						>
							Dashboard Analytics
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
							<FormControl size="small" sx={{ minWidth: 200 }}>
								<InputLabel id="page-selector-label">Dashboard View</InputLabel>
								<Select
									labelId="page-selector-label"
									value={selectedPage}
									onChange={handlePageChange}
									label="Dashboard View"
								>
									<MenuItem value="main">
										Supply Type Allocation Dashboard
									</MenuItem>
									<MenuItem value="vff">Vehicle Tracking Dashboard</MenuItem>
									{showSeriesDashboard && (
										<MenuItem value="series">Series Dashboard</MenuItem>
									)}
								</Select>
							</FormControl>
							<Button
								variant="contained"
								onClick={exportToPDF}
								sx={{
									backgroundColor: "#cc0000",
									fontFamily: "MAN EUROPE_Bold",
									"&:hover": { backgroundColor: "#aa0000" },
								}}
							>
								Export to PDF
							</Button>
							{selectedPage === "series" &&
								showSeriesDashboard &&
								seriesFloatingState.floatingMode && (
									<Button
										variant="contained"
										onClick={() => setFloatingExportOpen(true)}
										sx={{
											backgroundColor: "#1e2832",
											fontFamily: "MAN EUROPE_Bold",
											"&:hover": { backgroundColor: "#0f161d" },
										}}
									>
										Export Data
									</Button>
								)}
						</Box>
					</Box>
				</Box>
				<FloatingExportModal
					isOpen={floatingExportOpen}
					onClose={() => setFloatingExportOpen(false)}
					userEmail={
						localStorage.getItem("username") ||
						localStorage.getItem("email") ||
						""
					}
					currentFilters={{
						project_number: seriesFloatingState.projectNumber,
						alg: seriesFloatingState.alg,
						change_no: seriesFloatingState.changeNumber,
					}}
				/>
				{/* Error Alert */}
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
						<Button
							onClick={() =>
								selectedPage === "vff" ? fetchVffData() : fetchMainData()
							}
							sx={{ ml: 2 }}
							color="error"
						>
							Retry
						</Button>
					</Alert>
				)}{" "}
				{/* Dashboard Content */}{" "}
				{selectedPage === "main" ? (
					<SupplyTypeAllocationDashboard
						totalParts={totalParts}
						purchaseTypeCounts={purchaseTypeCounts}
						seTeamCounts={seTeamCounts}
						mainChartData={mainChartData}
						filterOptions={filterOptions}
						selectedTeams={selectedTeams}
						selectedProject={selectedProject}
						selectedProgramm={selectedProgramm}
						selectedPurchaseType={selectedPurchaseType}
						selectedCategory={selectedCategory}
						tempSelectedTeams={tempSelectedTeams}
						tempSelectedProject={tempSelectedProject}
						tempSelectedProgramm={tempSelectedProgramm}
						tempSelectedPurchaseType={tempSelectedPurchaseType}
						tempSelectedCategory={tempSelectedCategory}
						handleTeamChange={handleTeamChange}
						handleProjectChange={handleProjectChange}
						handleProgrammChange={handleProgrammChange}
						handlePurchaseTypeChange={handlePurchaseTypeChange}
						handleCategoryChange={handleCategoryChange}
						handleApplyFilters={handleApplyFilters}
						handleResetFilters={handleResetFilters}
						isChartLoading={isChartLoading}
						hasFiltersChanged={hasFiltersChanged}
					/>
				) : selectedPage === "vff" ? (
					<VffDashboard
						vffChartData={vffChartData}
						error={error}
						loading={loading}
						anflugTypes={anflugTypes}
						vehicleNumbers={filteredVehicleNumbers}
						programms={programms}
						vffPartNumbers={vffPartNumbers}
						vffPurchaseTypes={vffPurchaseTypes}
						selectedVffProgramm={selectedVffProgramm}
						selectedVehicles={selectedVehicles}
						selectedVffPartNo={selectedVffPartNo}
						selectedVffPurchaseType={selectedVffPurchaseType}
						tempSelectedVffProgramm={tempSelectedVffProgramm}
						tempSelectedVehicles={tempSelectedVehicles}
						tempSelectedVffPartNo={tempSelectedVffPartNo}
						tempSelectedVffPurchaseType={tempSelectedVffPurchaseType}
						handleVffProgrammChange={handleVffProgrammChange}
						handleVehicleChange={handleVehicleChange}
						handleVffPartNoChange={handleVffPartNoChange}
						handleVffPurchaseTypeChange={handleVffPurchaseTypeChange}
						handleApplyVffFilters={handleApplyVffFilters}
						handleResetVffFilters={handleResetVffFilters}
						fetchVffData={fetchVffData}
						totalVehicles={vffTotalVehicles}
						totalAnflugTypes={vffTotalAnflugTypes}
						totalParts={vffTotalParts}
						isChartLoading={isChartLoading}
						hasVffFiltersChanged={hasVffFiltersChanged}
					/>
				) : selectedPage === "series" && showSeriesDashboard ? (
					<SeriesDashboard onFloatingStateChange={setSeriesFloatingState} />
				) : (
					<SupplyTypeAllocationDashboard
						totalParts={totalParts}
						purchaseTypeCounts={purchaseTypeCounts}
						seTeamCounts={seTeamCounts}
						mainChartData={mainChartData}
						filterOptions={filterOptions}
						selectedTeams={selectedTeams}
						selectedProject={selectedProject}
						selectedProgramm={selectedProgramm}
						selectedPurchaseType={selectedPurchaseType}
						selectedCategory={selectedCategory}
						tempSelectedTeams={tempSelectedTeams}
						tempSelectedProject={tempSelectedProject}
						tempSelectedProgramm={tempSelectedProgramm}
						tempSelectedPurchaseType={tempSelectedPurchaseType}
						tempSelectedCategory={tempSelectedCategory}
						handleTeamChange={handleTeamChange}
						handleProjectChange={handleProjectChange}
						handleProgrammChange={handleProgrammChange}
						handlePurchaseTypeChange={handlePurchaseTypeChange}
						handleCategoryChange={handleCategoryChange}
						handleApplyFilters={handleApplyFilters}
						handleResetFilters={handleResetFilters}
						isChartLoading={isChartLoading}
						hasFiltersChanged={hasFiltersChanged}
					/>
				)}
				{/* Footer */}
				<Box
					sx={{
						mt: 4,
						pt: 2,
						borderTop: "1px solid #ddd",
						textAlign: "center",
					}}
				>
					<Typography variant="body2" sx={{ color: "#545454" }}>
						© {new Date().getFullYear()} MAN Trucks & Bus. All rights reserved.
					</Typography>
					<Typography
						variant="caption"
						sx={{ display: "block", mt: 1, color: "#545454" }}
					>
						This dashboard is confidential and intended for authorized personnel
						only.
					</Typography>
				</Box>
			</Box>
		</div>
	);
};

export default DashboardPage;
